'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  APP_RAIL_COLLAPSE_REQUEST_EVENT,
  APP_RAIL_COLLAPSED_WIDTH,
  APP_RAIL_EXPANDED_WIDTH,
  APP_RAIL_WIDTH_CSS_VARIABLE,
  persistAppRailCollapsedState,
  readAppRailCollapsedState,
} from './app-rail-state';

function useAppRailState() {
  const [editorAvailability, setEditorAvailability] = useState<{ userId: string; projectId: string; site: boolean; canvas: boolean } | null>(null);
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [pointerHovered, setRailHovered] = useState(false);
  const [openOverlays, setOpenOverlays] = useState(0);
  const [hoverDismissed, setHoverDismissed] = useState(false);
  const railHovered = !hoverDismissed && (pointerHovered || openOverlays > 0);
  const [railStateReady, setRailStateReady] = useState(false);
  const railHoverExitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRailHoverExit = useCallback(() => {
    if (railHoverExitTimer.current !== null) {
      clearTimeout(railHoverExitTimer.current);
      railHoverExitTimer.current = null;
    }
  }, []);
  const closeRailHover = useCallback(() => {
    clearRailHoverExit();
    setRailHovered(false);
  }, [clearRailHoverExit]);
  const handleRailPointerEnter = useCallback(
    (event: { pointerType: string }) => {
      clearRailHoverExit();
      if (event.pointerType !== 'touch') {
        setHoverDismissed(false);
        setRailHovered(true);
      }
    },
    [clearRailHoverExit],
  );
  const handleRailPointerLeave = useCallback(() => {
    clearRailHoverExit();
    railHoverExitTimer.current = setTimeout(closeRailHover, 120);
  }, [clearRailHoverExit, closeRailHover]);
  useEffect(() => clearRailHoverExit, [clearRailHoverExit]);

  const holdRailHover = useCallback(() => {
    setOpenOverlays(count => count + 1);
    return () => setOpenOverlays(count => count - 1);
  }, []);

  useLayoutEffect(() => {
    const collapsed = readAppRailCollapsedState();
    setRailCollapsed(collapsed);
    // Refresh shell geometry without persisting the responsive default as a user choice.
    document.documentElement.style.setProperty(
      APP_RAIL_WIDTH_CSS_VARIABLE,
      collapsed ? APP_RAIL_COLLAPSED_WIDTH : APP_RAIL_EXPANDED_WIDTH,
    );
    setRailStateReady(true);
  }, []);

  useEffect(() => {
    const collapseRail = () => {
      closeRailHover();
      setHoverDismissed(true);
      setRailCollapsed(true);
    };
    window.addEventListener(APP_RAIL_COLLAPSE_REQUEST_EVENT, collapseRail);
    return () => window.removeEventListener(APP_RAIL_COLLAPSE_REQUEST_EVENT, collapseRail);
  }, [closeRailHover]);

  const toggleRailCollapsed = useCallback(() => {
    const next = !railCollapsed;
    closeRailHover();
    setHoverDismissed(next);
    persistAppRailCollapsedState(next);
    setRailCollapsed(next);
  }, [railCollapsed, closeRailHover]);

  const railVisuallyCollapsed = railCollapsed && !railHovered;
  const railToggleLabel = railCollapsed
    ? railHovered
      ? 'Keep navigation expanded'
      : 'Expand navigation'
    : 'Collapse navigation';

  return useMemo(
    () => ({
      editorAvailability,
      setEditorAvailability,
      railCollapsed,
      railHovered,
      railStateReady,
      railVisuallyCollapsed,
      railToggleLabel,
      holdRailHover,
      closeRailHover,
      handleRailPointerEnter,
      handleRailPointerLeave,
      toggleRailCollapsed,
    }),
    [
      editorAvailability,
      setEditorAvailability,
      railCollapsed,
      railHovered,
      railStateReady,
      railVisuallyCollapsed,
      railToggleLabel,
      holdRailHover,
      closeRailHover,
      handleRailPointerEnter,
      handleRailPointerLeave,
      toggleRailCollapsed,
    ],
  );
}

const AppRailContext = createContext<ReturnType<typeof useAppRailState> | null>(null);

export function AppRailProvider({ children }: { children: ReactNode }) {
  const value = useAppRailState();
  return <AppRailContext.Provider value={value}>{children}</AppRailContext.Provider>;
}

export function useAppRail() {
  const context = useContext(AppRailContext);
  if (!context) throw new Error('useAppRail must be used within AppRailProvider');
  return context;
}

/** Rail-owned menus keep the flyout interactive across their portal and positioning gap. */
export function useAppRailOverlay(enabled = true) {
  const holdRailHover = useContext(AppRailContext)?.holdRailHover;
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (enabled && open) return holdRailHover?.();
  }, [enabled, open, holdRailHover]);
  return useCallback((details: { open: boolean }) => setOpen(details.open), []);
}
